import { useEffect, useState } from "react";
import { useSampleRules } from "@/api/sample-rules";
import { RuleBuilderDrawer } from "./components/rule-builder-drawer";
import { RulesListDrawer } from "./components/rules-list-drawer";
import { RulesListEmptyDrawer } from "./components/rules-list-empty-drawer";
import { RulesListLoadingDrawer } from "./components/rules-list-loading-drawer";

type View = { kind: "list" } | { kind: "builder"; ruleId?: string };

interface SampleRulesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Lets a caller that renders this drawer from inside another overlay (e.g. a modal step) push
  // it above that overlay's z-index; every SlideoutMenu here defaults to z-50 otherwise.
  overlayClassName?: string;
}

export function SampleRulesDrawer({ open, onOpenChange, overlayClassName }: SampleRulesDrawerProps) {
  const [view, setView] = useState<View>({ kind: "list" });
  const { data, isLoading } = useSampleRules();

  // Reopening the panel lands on the list, never back on an abandoned draft.
  useEffect(() => {
    if (!open) setView({ kind: "list" });
  }, [open]);

  const openBuilder = (ruleId?: string) => setView({ kind: "builder", ruleId });
  const backToList = () => setView({ kind: "list" });

  if (view.kind === "builder") {
    return (
      <RuleBuilderDrawer
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) backToList();
        }}
        ruleId={view.ruleId}
        onBack={backToList}
        overlayClassName={overlayClassName}
      />
    );
  }

  if (isLoading) {
    return <RulesListLoadingDrawer open={open} onOpenChange={onOpenChange} overlayClassName={overlayClassName} />;
  }

  if (!data?.items.length) {
    return (
      <RulesListEmptyDrawer
        open={open}
        onOpenChange={onOpenChange}
        onCreate={() => openBuilder()}
        overlayClassName={overlayClassName}
      />
    );
  }

  return (
    <RulesListDrawer
      open={open}
      onOpenChange={onOpenChange}
      onEdit={openBuilder}
      onCreate={() => openBuilder()}
      overlayClassName={overlayClassName}
    />
  );
}
